import { Body1, tokens } from '@fluentui/react-components';
import { IChatMessage } from '../../libs/models/ChatMessage';

/*
 * Function to check if date is today.
 */
export function isToday(date: Date) {
    return date.toDateString() !== new Date().toDateString();
}

/*
 * Function to render the date and/or time of a message.
 */
export function timestampToDateString(timestamp: number, alwaysShowTime = false) {
    const date = new Date(timestamp);
    const dateString = date.toLocaleDateString([], {
        month: 'numeric',
        day: 'numeric',
    });
    const timeString = date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
    });

    return date.toDateString() !== new Date().toDateString()
        ? alwaysShowTime
            ? dateString + ' ' + timeString // if the date is not today and we are always showing the time, show the date and time
            : dateString // if the date is not today and we are not always showing the time, only show the date
        : timeString; // if the date is today, only show the time
}

/*
 * Function to create a command link
 */
export function createCommandLink(command: string) {
    const escapedCommand = encodeURIComponent(command);
    const createCommandLink = `<span style="text-decoration: underline; cursor: pointer" data-command="${escapedCommand}" onclick="(function(){ let chatInput = document.getElementById('chat-input'); chatInput.value = decodeURIComponent('${escapedCommand}'); chatInput.focus(); return false; })();return false;">${command}</span>`;
    return createCommandLink;
}

/*
 * Function to format chat text content to remove any html tags from it.
 */
export function formatChatTextContent(messageContent: string) {
    const contentAsString = messageContent
        .trim()
        .replace(/^sk:\/\/.*$/gm, (match: string) => createCommandLink(match))
        .replace(/^!sk:.*$/gm, (match: string) => createCommandLink(match));
    return contentAsString;
}

/*
 * Formats text containing `\n` or `\r` into paragraphs.
 */
export function formatParagraphTextContent(messageContent = '') {
    messageContent = messageContent.replaceAll('\r\n', '\n\r');

    return (
        <Body1>
            {messageContent.split('\n').map((paragraph, idx) => (
                <p
                    key={`paragraph-${idx}`}
                    style={
                        paragraph.includes('\r')
                            ? {
                                  display: 'flex',
                                  marginLeft: tokens.spacingHorizontalL,
                              }
                            : {
                                  overflowWrap: 'anywhere',
                              }
                    }
                >
                    {paragraph}
                </p>
            ))}
        </Body1>
    );
}

/*
 * Function to replace citation links with indices matching the citation list.
 * Also handles complex citation patterns like [12:3†source] and 【20:0†source】 and simplifies them to [1], [2], etc.
 */
export function replaceCitationLinksWithIndices(formattedMessageContent: string, message: IChatMessage) {
    const citations = message.citations;
    if (!citations || citations.length === 0) {
        return formattedMessageContent;
    }

    // First pass: Replace exact citation links with their indices
    citations.forEach((citation, index) => {
        const citationLink = citation.link;
        const simpleIndex = `[${index + 1}]`;
        // Use replace with global flag instead of replaceAll for compatibility
        const linkRegex = new RegExp(citationLink.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        formattedMessageContent = formattedMessageContent.replace(linkRegex, simpleIndex);
    });

    // Second pass: Handle complex citation patterns
    // For patterns like 【20:0†source】, we need to map them to the correct citation indices
    // The challenge is that multiple citations might have the same sourceName
    
    // Helper function to find all matches (alternative to matchAll)
    function findAllMatches(text: string, regex: RegExp): RegExpExecArray[] {
        const matches: RegExpExecArray[] = [];
        let match;
        while ((match = regex.exec(text)) !== null) {
            matches.push(match);
            if (!regex.global) break;
        }
        return matches;
    }
    
    // Find all citation patterns in the text first
    const allCitationPatterns = [
        ...findAllMatches(formattedMessageContent, /【(\d+):(\d+)†([^】]+)】/g),
        ...findAllMatches(formattedMessageContent, /\[(\d+):(\d+)†([^\]]+)\]/g),
        ...findAllMatches(formattedMessageContent, /【(\d+):(\d+)\s+([^】]+)】/g),
        ...findAllMatches(formattedMessageContent, /\[(\d+):(\d+)\s+([^\]]+)\]/g)
    ];
    
    // Create a mapping of unique citation patterns to citation indices
    const uniquePatterns: Record<string, boolean> = {};
    const patternToCitationIndex: Record<string, number> = {};
    
    allCitationPatterns.forEach(match => {
        const firstNum = parseInt(match[1], 10);
        const secondNum = parseInt(match[2], 10);
        const sourceName = match[3].trim();
        
        // Create a unique key for this pattern
        const patternKey = `${firstNum}:${secondNum}:${sourceName}`;
        
        if (!uniquePatterns[patternKey]) {
            uniquePatterns[patternKey] = true;
            
            // Try to find a citation that matches this pattern
            let citationIndex = -1;
            
            // Get already used citation indices
            const usedIndices = Object.keys(patternToCitationIndex).map(k => patternToCitationIndex[k]);
            
            // First, try to match by sourceName
            for (let i = 0; i < citations.length; i++) {
                if (citations[i].sourceName === sourceName && !usedIndices.includes(i)) {
                    citationIndex = i;
                    break;
                }
            }
            
            // If no unused citation found with matching sourceName, use sequential assignment
            if (citationIndex === -1) {
                citationIndex = usedIndices.length % citations.length;
            }
            
            patternToCitationIndex[patternKey] = citationIndex;
        }
    });
    
    // Third pass: Replace all citation patterns with their mapped indices
    // Japanese brackets with dagger: 【number:number†sourceName】
    formattedMessageContent = formattedMessageContent.replace(/【(\d+):(\d+)†([^】]+)】/g, (_: string, firstNum: string, secondNum: string, sourceName: string) => {
        const patternKey = `${firstNum}:${secondNum}:${sourceName.trim()}`;
        const citationIndex = patternToCitationIndex[patternKey] ?? 0;
        return `[${citationIndex + 1}]`;
    });
    
    // Regular brackets with dagger: [number:number†sourceName]
    formattedMessageContent = formattedMessageContent.replace(/\[(\d+):(\d+)†([^\]]+)\]/g, (_: string, firstNum: string, secondNum: string, sourceName: string) => {
        const patternKey = `${firstNum}:${secondNum}:${sourceName.trim()}`;
        const citationIndex = patternToCitationIndex[patternKey] ?? 0;
        return `[${citationIndex + 1}]`;
    });
    
    // Japanese brackets without dagger: 【number:number sourceName】
    formattedMessageContent = formattedMessageContent.replace(/【(\d+):(\d+)\s+([^】]+)】/g, (_: string, firstNum: string, secondNum: string, sourceName: string) => {
        const patternKey = `${firstNum}:${secondNum}:${sourceName.trim()}`;
        const citationIndex = patternToCitationIndex[patternKey] ?? 0;
        return `[${citationIndex + 1}]`;
    });
    
    // Regular brackets without dagger: [number:number sourceName]
    formattedMessageContent = formattedMessageContent.replace(/\[(\d+):(\d+)\s+([^\]]+)\]/g, (_: string, firstNum: string, secondNum: string, sourceName: string) => {
        const patternKey = `${firstNum}:${secondNum}:${sourceName.trim()}`;
        const citationIndex = patternToCitationIndex[patternKey] ?? 0;
        return `[${citationIndex + 1}]`;
    });

    return formattedMessageContent;
}

/**
 * Gets message of error
 */
export function getErrorDetails(error: unknown) {
    return error instanceof Error ? error.message : String(error);
}
